/**
 * Native payday / flash-sale product grid (Shopify-native product discovery mode).
 *
 * Replaces the Bloomreach payday renderer (assets/bloomreach-payday-renderer.js, source
 * bloomreach-app/src/apps/payday) inside .hm-payday-deals__results of sections/hm-payday-deals.liquid.
 * Spec: docs/bloomreach-shopify-migration/native-parity-contract-predictive.md, section B.
 *
 * Selection (same inputs as Bloomreach preparePaydayState):
 *   - payday_categories=<id,id,...> in the page URL (written by the section's nav controller), else
 *   - every leaf collection of the active nav (?tab=<key>, else the default nav, else the first).
 * Bloomreach leaf ids are Shopify collection ids, so one id maps to exactly one collection.
 * Several ids ("View all") cannot be unioned by Shopify: the nav's native "all" collection
 * (payday_nav metaobject field native_all_collection, else the section setting
 * native_all_collection) is used, falling back to the first selected collection.
 *
 * Rendering: the grid is server-rendered by sections/hm-native-payday-grid.liquid through the
 * Section Rendering API on the collection URL (/collections/{handle}?section_id=...). Filters, sort,
 * pagination / infinite scroll and the layout toggle reuse NativeResultsController
 * (assets/hm-native-results.js); this module only maps the payday page URL to the collection URL,
 * performs the initial load (Bloomreach also renders client-side after a skeleton) and reloads on
 * popstate, which the payday nav controller dispatches on every tab / leaf change.
 */

/** Page params owned by the payday nav controller; never forwarded to the collection URL. */
export const PAYDAY_PARAMS = ['payday_categories', 'payday_group', 'type', 'section_id'];

/** Debug logs are on when the page URL has ?fsn_debug (flash sale / payday grid troubleshooting). */
function debugEnabled() {
  try {
    return new URL(window.location.href).searchParams.has('fsn_debug');
  } catch {
    return false;
  }
}

function debugLog(...args) {
  if (debugEnabled()) console.log('[FlashSale grid]', ...args);
}

/** Reads JSON from a <script type="application/json"> element. */
export function readJson(element, fallback) {
  if (!element) return fallback;
  try {
    return JSON.parse(element.textContent || 'null') ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Active nav and selected collection ids for a page URL (bloomreach-app payday/state.ts
 * paydayCategoryIds, with the section's ?tab / default-nav resolution).
 * @param {string} href
 * @param {{queryParam?: string, defaultNav?: string, navs: Array<{key: string, collections: Array<{id: string}>}>}} config
 */
export function paydaySelection(href, config) {
  const url = new URL(href, window.location.href);
  const navs = Array.isArray(config?.navs) ? config.navs : [];
  const queryParam = config?.queryParam || 'tab';
  const requested = url.searchParams.get(queryParam);
  const nav =
    navs.find((item) => String(item.key) === requested) ??
    navs.find((item) => String(item.key) === String(config?.defaultNav ?? '')) ??
    navs[0];
  const explicit = (url.searchParams.get('payday_categories') ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  const ids = explicit.length
    ? [...new Set(explicit)]
    : [...new Set((nav?.collections ?? []).map((collection) => String(collection.id)).filter(Boolean))];
  return { nav, ids };
}

/**
 * Collection handle for a selection: one id -> its collection; several ids -> the nav's native
 * "all" collection, else the first id that resolves.
 */
export function selectionHandle(selection, config) {
  const byId = new Map();
  for (const nav of config?.navs ?? []) {
    for (const collection of nav.collections ?? []) {
      if (collection?.id && collection?.handle) byId.set(String(collection.id), String(collection.handle));
    }
  }
  const { nav, ids } = selection;
  if (ids.length === 1) return byId.get(ids[0]) ?? null;
  if (ids.length > 1) {
    const all = nav?.allCollection || config?.allCollection;
    if (all) return String(all);
    for (const id of ids) if (byId.has(id)) return byId.get(id);
  }
  return null;
}

/**
 * Collection page URL carrying the grid params of the payday page URL (page, sort_by, filter.*).
 * @returns {string | null} relative URL, or null when the selection has no collection
 */
export function paydayCollectionUrl(href, config) {
  const handle = selectionHandle(paydaySelection(href, config), config);
  if (!handle || !/^[a-z0-9][a-z0-9_-]*$/i.test(handle)) return null;
  const source = new URL(href, window.location.href);
  const params = new URLSearchParams(source.search);
  params.delete(config?.queryParam || 'tab');
  for (const name of PAYDAY_PARAMS) params.delete(name);
  const root = String(config?.root || '/').replace(/\/?$/, '/');
  const search = params.toString();
  return `${root}collections/${encodeURIComponent(handle)}${search ? `?${search}` : ''}`;
}

/**
 * Builds the payday controller on top of NativeResultsController.
 * @param {typeof import('./hm-native-results.js').NativeResultsController} Base
 */
export function createPaydayController(Base) {
  return class PaydayResultsController extends Base {
    constructor(root, host) {
      super(root, host);
      this.payday = readJson(host.querySelector('[data-hm-native-payday-config]'), { navs: [] });
    }

    connect() {
      super.connect();
      /* A sale collection that is deleted / not on the Online Store 404s: show the empty state, not error + retry. */
      this.root.addEventListener('hm-native-results:error', (event) => {
        const { mode, error } = event.detail ?? {};
        if (mode !== 'append' && / 404$/.test(String(error?.message ?? ''))) this.renderEmpty();
      });
      if (debugEnabled()) {
        debugLog('config', this.payday);
        const metaobjects = readJson(document.querySelector('[data-fsn-debug]'), null);
        if (metaobjects) debugLog('metaobjects (server side)', metaobjects);
        this.root.addEventListener('hm-native-results:error', (event) => debugLog('request failed', event.detail));
      }
      this.load(window.location.href);
    }

    /** @param {string} url - payday page URL */
    load(url, options) {
      const target = paydayCollectionUrl(url, this.payday);
      if (debugEnabled()) {
        const selection = paydaySelection(url, this.payday);
        debugLog('load', {
          pageUrl: url,
          navKey: selection.nav?.key ?? null,
          navCollections: selection.nav?.collections ?? [],
          selectedIds: selection.ids,
          handle: selectionHandle(selection, this.payday),
          sectionId: this.sectionId,
          target,
        });
        if (!target) debugLog('no collection for this slot -> renderEmpty() (check sale_collections of the metaobject)');
      }
      if (!target) {
        this.controller?.abort();
        this.renderEmpty();
        return Promise.resolve();
      }
      const pending = super.load(target, options);
      if (debugEnabled()) {
        pending.then(() => {
          debugLog('rendered', {
            resultsCount: this.root.dataset.resultsCount ?? null,
            productCards: this.root.querySelectorAll('product-card:not([data-bloomreach-product-skeleton])').length,
            emptyMessage: this.root.querySelector('.collection__empty, .hm-search-no-results')?.textContent?.trim() ?? null,
          });
        });
      }
      return pending;
    }

    /** The section response carries the full results config (facet value labels): adopt it. */
    applyReplace(nextRoot) {
      if (debugEnabled()) {
        const doc = nextRoot.ownerDocument;
        debugLog('section response', {
          collection: readJson(doc?.querySelector('[data-fsn-grid-debug]'), 'no data-fsn-grid-debug (not the flash sale grid section?)'),
          resultsCountAttr: nextRoot.dataset.resultsCount ?? null,
          productCards: nextRoot.querySelectorAll('product-card:not([data-bloomreach-product-skeleton])').length,
        });
      }
      const config = readJson(nextRoot.ownerDocument?.querySelector('[data-hm-native-results-config]'), null);
      if (config?.labels) {
        this.labels = { ...this.labels, ...config.labels };
        this.facetValues = this.labels.facetValues ?? this.facetValues;
      }
      super.applyReplace(nextRoot);
    }

    /**
     * renderEmpty / renderError replace the whole root, so the base renderLoading finds no results main
     * and the stale message stays up while the next request loads. Rebuild the main first so the
     * skeleton shows (applyReplace swaps it out when the response lands).
     */
    renderLoading() {
      if (!this.main()) {
        if (this.facetsOpen) this.resetOpenState();
        const main = document.createElement('div');
        main.className = 'hm-results-page__main collection__main';
        main.setAttribute('data-collection-section-main', 'collection__main--facets-open');
        main.setAttribute('data-bloomreach-results-page', '');
        this.root.replaceChildren(main);
      }
      super.renderLoading();
    }

    /** No collection for the selection: the Bloomreach empty payday state. */
    renderEmpty() {
      if (this.facetsOpen) this.resetOpenState();
      this.root.innerHTML = `<div class="hm-search-no-results"><p class="hm-search-no-results__message" role="status" data-bloomreach-results-page></p></div>`;
      const message = this.root.querySelector('.hm-search-no-results__message');
      if (message) message.textContent = this.labels.empty ?? '';
    }
  };
}

export class HmNativePaydayResults extends HTMLElement {
  async connectedCallback() {
    if (this.controller || this.connecting) return;
    const root =
      (this.previousElementSibling?.matches('[data-bloomreach-renderer-root]') ? this.previousElementSibling : null) ??
      this.parentElement?.querySelector('[data-bloomreach-renderer-root]');
    if (!root || !this.dataset.resultsModule) return;
    this.connecting = true;
    try {
      const { NativeResultsController } = await import(this.dataset.resultsModule);
      if (!this.isConnected) return;
      const Controller = createPaydayController(NativeResultsController);
      this.controller = new Controller(root, this);
      this.controller.connect();
    } finally {
      this.connecting = false;
    }
  }

  disconnectedCallback() {
    this.controller?.disconnect();
    this.controller = undefined;
  }
}

if (!customElements.get('hm-native-payday-results')) {
  customElements.define('hm-native-payday-results', HmNativePaydayResults);
}
